import React from 'react'
import { Link } from '@tanstack/react-router'

/**
 * Which accent slot the tile uses. In the unified theme system there
 * are only two real colors — `accent` (primary) and `second`
 * (secondary) — so tiles either read as "primary" or "secondary"
 * within whatever theme is active. App distinction comes from icon
 * + label, not color.
 */
export type AppTileAccent = 'accent' | 'second'

interface AppTileProps {
  /** Single-letter or short monogram shown in the colored icon badge. */
  icon: string
  /** App name. */
  title: string
  /** One-line description under the title. */
  description: string
  /** Route the tile navigates to on click. */
  href: string
  /** Which accent slot the icon badge + hover border read from. */
  accent: AppTileAccent
}

const ACCENT_STYLES: Record<AppTileAccent, { icon: string; border: string }> = {
  accent: {
    icon: 'bg-accent-muted text-accent',
    border:
      'hover:border-[color:var(--accent-halo)] hover:shadow-[0_0_0_1px_var(--accent-halo)]',
  },
  second: {
    icon: 'bg-[color:var(--second-soft)] text-second',
    border: 'hover:border-second hover:shadow-[0_0_0_1px_var(--second-soft)]',
  },
}

/**
 * One of the apps on the Home launcher. Visual shell is shared so
 * adding a new app is just another <AppTile>.
 */
export const AppTile: React.FC<AppTileProps> = ({
  icon,
  title,
  description,
  href,
  accent,
}) => {
  const styles = ACCENT_STYLES[accent]
  return (
    <Link
      to={href}
      className={`block rounded-xl bg-surface-raised border border-brd-subtle p-5 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-medium ${styles.border}`}
    >
      <span
        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg mb-3 font-bold text-lg ${styles.icon}`}
      >
        {icon}
      </span>
      <div className="text-sm font-semibold text-content-primary">{title}</div>
      <div className="mt-1 text-xs text-content-secondary leading-relaxed">
        {description}
      </div>
    </Link>
  )
}
